import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  selectProjectsWithSaveInProgress,
  updateMapSaveStatus,
  clearMapSaveStatus,
} from "../features/mapSaveStatus/mapSaveStatusSlice";
import { invalidateProjectAreaStatus, selectCurrentProject } from "../features/project/projectSlice";
import { fetchMapSaveStatus } from "../services/helperBackendApiService";
import { useToggleProjectLockMutation } from "../features/project/projectApiSlice";
import { triggerMsaCalculation } from "../services/msaApiService";

const POLL_INTERVAL_MS = 3500;
const SUCCESS_CLEAR_DELAY_MS = 5000;

/**
 * Hook that polls the helper backend for map save status when a project
 * is in the in_progress state. Only polls for the project currently being
 * viewed; when the user switches project or leaves, polling for others stops.
 */
export function useMapSaveStatusPolling() {
  const dispatch = useAppDispatch();
  const [toggleProjectLock] = useToggleProjectLockMutation();
  const projectsInProgress = useAppSelector(selectProjectsWithSaveInProgress);
  const { project: currentProject } = useAppSelector(selectCurrentProject);
  const currentProjectId =
    currentProject?.id != null ? String(currentProject.id) : null;
  const intervalRefs = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  );
  const clearTimeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Start polling only for the current project when it has save in progress
  useEffect(() => {
    const projectsToPoll =
      currentProjectId && projectsInProgress.includes(currentProjectId)
        ? [currentProjectId]
        : [];

    for (const projectId of projectsToPoll) {
      if (intervalRefs.current.has(projectId)) {
        continue;
      }

      const poll = async () => {
        try {
          const result = await fetchMapSaveStatus(projectId);

          if (result === "SAVE_IN_PROGRESS" || result === "UNSAVED") {
            return;
          }

          if (result === "SAVE_SUCCESS") {
            const intervalId = intervalRefs.current.get(projectId);
            if (intervalId) {
              clearInterval(intervalId);
              intervalRefs.current.delete(projectId);
            }

            dispatch(
              updateMapSaveStatus({
                projectId,
                status: "success",
              }),
            );

            try {
              await toggleProjectLock({
                projectId: Number(projectId),
                isLocked: false,
              }).unwrap();
            } catch (unlockError) {
              console.warn(
                "Failed to unlock project after map save:",
                unlockError,
              );
            }

            dispatch(invalidateProjectAreaStatus());
            triggerMsaCalculation(projectId, "project", dispatch);

            const clearTimeoutId = setTimeout(() => {
              dispatch(clearMapSaveStatus({ projectId }));
              clearTimeoutRefs.current.delete(projectId);
            }, SUCCESS_CLEAR_DELAY_MS);
            clearTimeoutRefs.current.set(projectId, clearTimeoutId);
            return;
          }

          if (
            result === "SAVE_ERROR" ||
            (typeof result === "object" && "error" in result)
          ) {
            const intervalId = intervalRefs.current.get(projectId);
            if (intervalId) {
              clearInterval(intervalId);
              intervalRefs.current.delete(projectId);
            }

            const errorMessage =
              typeof result === "object"
                ? result.error
                : "An error occurred while saving the map";
            dispatch(
              updateMapSaveStatus({
                projectId,
                status: "error",
                errorMessage,
              }),
            );
          }
        } catch (err) {
          const intervalId = intervalRefs.current.get(projectId);
          if (intervalId) {
            clearInterval(intervalId);
            intervalRefs.current.delete(projectId);
          }

          const errorMessage =
            err instanceof Error
              ? err.message
              : "Network error while checking save status";
          dispatch(
            updateMapSaveStatus({
              projectId,
              status: "error",
              errorMessage,
            }),
          );
        }
      };

      const intervalId = setInterval(poll, POLL_INTERVAL_MS);
      intervalRefs.current.set(projectId, intervalId);
      poll();
    }

    const keepPollingIds = new Set(projectsToPoll);
    for (const [projectId, intervalId] of intervalRefs.current) {
      if (!keepPollingIds.has(projectId)) {
        clearInterval(intervalId);
        intervalRefs.current.delete(projectId);
      }
    }
  }, [projectsInProgress, currentProjectId, dispatch, toggleProjectLock]);

  // Cleanup interval and timeout references on unmount
  useEffect(() => {
    const intervals = intervalRefs.current;
    const timeouts = clearTimeoutRefs.current;
    return () => {
      for (const intervalId of intervals.values()) {
        clearInterval(intervalId);
      }
      intervals.clear();
      for (const timeoutId of timeouts.values()) {
        clearTimeout(timeoutId);
      }
      timeouts.clear();
    };
  }, []);
}
